import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Trophy, LogOut, Bell, Menu, X, Heart, ChevronDown } from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Navbar = ({ user, role, onLogout }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [aboutMenuOpen, setAboutMenuOpen] = useState(false);
  const [pageSettings, setPageSettings] = useState({
    show_about_us: true,
    show_careers: true,
    show_faq: true,
    show_contact_us: true
  });

  useEffect(() => {
    fetchPageSettings();
    if (role === 'customer') {
      fetchUnreadCount();
      // Poll every 30 seconds for new notifications
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [role]);

  const fetchPageSettings = async () => {
    try {
      const response = await axios.get(`${API}/public/settings`);
      setPageSettings({
        show_about_us: response.data.show_about_us ?? true,
        show_careers: response.data.show_careers ?? true,
        show_faq: response.data.show_faq ?? true,
        show_contact_us: response.data.show_contact_us ?? true
      });
    } catch (error) {
      console.error('Error fetching page settings:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const unread = response.data.filter(n => !n.is_read).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('role');
    if (onLogout) onLogout();
    navigate('/');
    // Force page reload to clear all state
    window.location.href = '/';
  };

  return (
    <nav className="bg-white border-b border-stone-200 sticky top-0 z-50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
            <Trophy className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
            <span className="font-playfair font-bold text-xl sm:text-2xl text-primary">DogtyDraw</span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-2 lg:space-x-4">
            {/* About Dropdown - Only show if at least one page is visible */}
            {(pageSettings.show_about_us || pageSettings.show_contact_us || pageSettings.show_faq || pageSettings.show_careers) && (
              <div className="relative">
                <Button
                  onClick={() => setAboutMenuOpen(!aboutMenuOpen)}
                  variant="ghost"
                  size="sm"
                  className="flex items-center gap-1"
                  data-testid="about-dropdown-button"
                >
                  <span className="text-sm font-outfit text-gray-700 font-medium">About</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${aboutMenuOpen ? 'rotate-180' : ''}`} />
                </Button>
              
              {aboutMenuOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setAboutMenuOpen(false)}
                  ></div>
                  <div className="absolute top-full right-0 mt-2 w-48 bg-white border border-stone-200 rounded-lg shadow-lg z-20 py-2">
                    {pageSettings.show_about_us && (
                      <button
                        onClick={() => {
                          navigate('/about-us');
                          setAboutMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm font-outfit text-gray-700 hover:bg-stone-50"
                      >
                        About Us
                      </button>
                    )}
                    {pageSettings.show_contact_us && (
                      <button
                        onClick={() => {
                          navigate('/contact-us');
                          setAboutMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm font-outfit text-gray-700 hover:bg-stone-50"
                      >
                        Contact Us
                      </button>
                    )}
                    {pageSettings.show_faq && (
                      <button
                        onClick={() => {
                          navigate('/faq');
                          setAboutMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm font-outfit text-gray-700 hover:bg-stone-50"
                      >
                        FAQ
                      </button>
                    )}
                    {pageSettings.show_careers && (
                      <button
                        onClick={() => {
                          navigate('/careers');
                          setAboutMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm font-outfit text-gray-700 hover:bg-stone-50"
                      >
                        Careers
                      </button>
                    )}
                  </div>
                </>
              )}
              </div>
            )}
            
            {user ? (
              <>
                <Button
                  onClick={() => {
                    if (role === 'customer') navigate('/customer/events');
                    else if (role === 'business') navigate('/business/dashboard');
                    else if (role === 'admin') navigate('/admin/dashboard');
                  }}
                  variant="ghost"
                  size="sm"
                  data-testid="dashboard-button"
                >
                  <span className="text-sm font-outfit text-gray-700 font-medium">Dashboard</span>
                </Button>
                {role === 'customer' && (
                  <>
                    <Button
                      onClick={() => navigate('/customer/wishlist')}
                      variant="ghost"
                      size="sm"
                      data-testid="wishlist-button"
                    >
                      <Heart className="w-4 h-4 lg:mr-2" />
                      <span className="hidden lg:inline text-sm font-outfit text-gray-700 font-medium">Wishlist</span>
                    </Button>
                    <Button
                      onClick={() => navigate('/customer/notifications')}
                      variant="ghost"
                      size="sm"
                      data-testid="notifications-button"
                      className="relative"
                    >
                      <Bell className="w-4 h-4 lg:mr-2" />
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                      <span className="hidden lg:inline text-sm font-outfit text-gray-700 font-medium">Notifications</span>
                    </Button>
                  </>
                )}
                <Button
                  onClick={() => navigate(role === 'business' ? '/business/profile' : role === 'admin' ? '/admin/dashboard' : '/customer/profile')}
                  variant="ghost"
                  size="sm"
                  data-testid="profile-button"
                  className="max-w-[150px] lg:max-w-none"
                >
                  <span className="text-sm font-outfit text-gray-600 truncate">{user.full_name || user.business_name || user.name}</span>
                </Button>
                <Button
                  onClick={handleLogout}
                  variant="ghost"
                  size="sm"
                  data-testid="logout-button"
                >
                  <LogOut className="w-4 h-4 lg:mr-2" />
                  <span className="hidden lg:inline">Logout</span>
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => navigate('/login')}
                  variant="ghost"
                  size="sm"
                  data-testid="login-button"
                >
                  Login
                </Button>
                <Button
                  onClick={() => navigate('/register')}
                  className="bg-primary hover:bg-primary/90 text-white"
                  size="sm"
                  data-testid="register-button"
                >
                  Get Started
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-2 border-t border-stone-200">
            {/* About Submenu - Mobile */}
            {(pageSettings.show_about_us || pageSettings.show_contact_us || pageSettings.show_faq || pageSettings.show_careers) && (
              <div className="border-b border-stone-100 pb-2 mb-2">
                <div className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">About</div>
                {pageSettings.show_about_us && (
                  <Button
                    onClick={() => {
                      navigate('/about-us');
                      setMobileMenuOpen(false);
                    }}
                    variant="ghost"
                    className="w-full justify-start pl-4"
                  >
                    About Us
                  </Button>
                )}
                {pageSettings.show_contact_us && (
                  <Button
                    onClick={() => {
                      navigate('/contact-us');
                      setMobileMenuOpen(false);
                    }}
                    variant="ghost"
                    className="w-full justify-start pl-4"
                  >
                    Contact Us
                  </Button>
                )}
                {pageSettings.show_faq && (
                  <Button
                    onClick={() => {
                      navigate('/faq');
                      setMobileMenuOpen(false);
                    }}
                    variant="ghost"
                    className="w-full justify-start pl-4"
                  >
                    FAQ
                  </Button>
                )}
                {pageSettings.show_careers && (
                  <Button
                    onClick={() => {
                      navigate('/careers');
                      setMobileMenuOpen(false);
                    }}
                    variant="ghost"
                    className="w-full justify-start pl-4"
                  >
                    Careers
                  </Button>
                )}
              </div>
            )}

            {user ? (
              <>
                <Button
                  onClick={() => {
                    if (role === 'customer') navigate('/customer/events');
                    else if (role === 'business') navigate('/business/dashboard');
                    else if (role === 'admin') navigate('/admin/dashboard');
                    setMobileMenuOpen(false);
                  }}
                  variant="ghost"
                  className="w-full justify-start"
                  data-testid="mobile-dashboard-button"
                >
                  Dashboard
                </Button>
                {role === 'customer' && (
                  <>
                    <Button
                      onClick={() => {
                        navigate('/customer/wishlist');
                        setMobileMenuOpen(false);
                      }}
                      variant="ghost"
                      className="w-full justify-start"
                      data-testid="mobile-wishlist-button"
                    >
                      <Heart className="w-4 h-4 mr-2" />
                      Wishlist
                    </Button>
                    <Button
                      onClick={() => {
                        navigate('/customer/notifications');
                        setMobileMenuOpen(false);
                      }}
                      variant="ghost"
                      className="w-full justify-start relative"
                      data-testid="mobile-notifications-button"
                    >
                      <Bell className="w-4 h-4 mr-2" />
                      Notifications
                      {unreadCount > 0 && (
                        <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-2 py-0.5 font-bold">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </Button>
                  </>
                )}
                <Button
                  onClick={() => {
                    navigate(role === 'business' ? '/business/profile' : role === 'admin' ? '/admin/dashboard' : '/customer/profile');
                    setMobileMenuOpen(false);
                  }}
                  variant="ghost"
                  className="w-full justify-start"
                  data-testid="mobile-profile-button"
                >
                  Profile ({user.full_name || user.business_name || user.name})
                </Button>
                <Button
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  variant="ghost"
                  className="w-full justify-start text-red-600"
                  data-testid="mobile-logout-button"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => {
                    navigate('/login');
                    setMobileMenuOpen(false);
                  }}
                  variant="ghost"
                  className="w-full justify-start"
                  data-testid="mobile-login-button"
                >
                  Login
                </Button>
                <Button
                  onClick={() => {
                    navigate('/register');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full bg-primary hover:bg-primary/90 text-white"
                  data-testid="mobile-register-button"
                >
                  Get Started
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};